import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { ACTIONS, EVENTS, Joyride, STATUS } from 'react-joyride';
import {
  buildHelpManualTourSteps,
  buildModuleTourSteps,
  findHelpModuleByKey,
  findHelpModuleByPath
} from 'components/help/helpRegistry';

const TOUR_STORAGE_KEY = 'gestodo:app-tour-completed';
const TOUR_START_EVENT = 'gestodo:start-app-tour';
const PENDING_MODULE_TOUR_KEY = 'gestodo:pending-module-tour';

const isBrowser = typeof window !== 'undefined';

const AppJoyride = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [run, setRun] = useState(false);
  const [tourKey, setTourKey] = useState(0);
  const [activeTourKey, setActiveTourKey] = useState('app');
  const [activeModuleKey, setActiveModuleKey] = useState(null);

  const startTour = useCallback((tourKeyValue = 'app', moduleKey = null) => {
    setRun(false);
    window.setTimeout(() => {
      if (tourKeyValue === 'module' && moduleKey === 'posts') {
        window.dispatchEvent(
          new CustomEvent('gestodo:posts:set-layout', {
            detail: { layout: 'list' }
          })
        );
      }
      setActiveTourKey(tourKeyValue);
      setActiveModuleKey(moduleKey);
      setTourKey(previous => previous + 1);
      setRun(true);
    }, 100);
  }, []);

  useEffect(() => {
    if (!isBrowser) return undefined;

    const hasCompletedTour = window.localStorage.getItem(TOUR_STORAGE_KEY) === 'true';
    const startOnRequest = event => {
      startTour(event.detail?.tourKey || 'app', event.detail?.moduleKey || null);
    };

    window.addEventListener(TOUR_START_EVENT, startOnRequest);
    if (!hasCompletedTour && !window.sessionStorage.getItem(PENDING_MODULE_TOUR_KEY)) {
      window.setTimeout(startTour, 700);
    }

    return () => window.removeEventListener(TOUR_START_EVENT, startOnRequest);
  }, [startTour]);

  useEffect(() => {
    if (!isBrowser) return undefined;

    const rawPendingTour = window.sessionStorage.getItem(PENDING_MODULE_TOUR_KEY);
    if (!rawPendingTour) return undefined;

    let pendingTour;
    try {
      pendingTour = JSON.parse(rawPendingTour);
    } catch (error) {
      window.sessionStorage.removeItem(PENDING_MODULE_TOUR_KEY);
      return undefined;
    }

    const targetModule = findHelpModuleByKey(pendingTour?.moduleKey);
    const currentModule = findHelpModuleByPath(pathname);
    if (!targetModule) {
      window.sessionStorage.removeItem(PENDING_MODULE_TOUR_KEY);
      return undefined;
    }
    if (currentModule?.key !== targetModule.key) {
      return undefined;
    }

    const launchWhenReady = () => {
      const readySelector = targetModule.tourReadySelector;
      if (!readySelector || document.querySelector(readySelector)) {
        window.sessionStorage.removeItem(PENDING_MODULE_TOUR_KEY);
        startTour('module', targetModule.key);
        return true;
      }
      return false;
    };

    if (launchWhenReady()) {
      return undefined;
    }

    const observer = new MutationObserver(() => {
      if (launchWhenReady()) {
        observer.disconnect();
      }
    });
    observer.observe(document.body, {
      attributes: true,
      childList: true,
      subtree: true
    });
    const timeoutId = window.setTimeout(() => observer.disconnect(), 15000);

    return () => {
      observer.disconnect();
      window.clearTimeout(timeoutId);
    };
  }, [pathname, startTour]);

  const appTourSteps = useMemo(
    () => [
      {
        target: '[data-tour="top-search"]',
        title: t('APP_TOUR.TOP_SEARCH_TITLE'),
        content: t('APP_TOUR.TOP_SEARCH_CONTENT'),
        placement: 'bottom',
        skipBeacon: true
      },
      {
        target: '[data-tour="context-switcher"]',
        title: t('APP_TOUR.CONTEXT_SWITCHER_TITLE'),
        content: t('APP_TOUR.CONTEXT_SWITCHER_CONTENT'),
        placement: 'bottom'
      },
      {
        target: '[data-tour="sidebar"]',
        title: t('APP_TOUR.SIDEBAR_TITLE'),
        content: t('APP_TOUR.SIDEBAR_CONTENT'),
        placement: 'right'
      },
      {
        target: '[data-tour="top-actions"]',
        title: t('APP_TOUR.TOP_ACTIONS_TITLE'),
        content: t('APP_TOUR.TOP_ACTIONS_CONTENT'),
        placement: 'bottom-end'
      }
    ],
    [t]
  );

  const moduleTourSteps = useMemo(() => {
    const currentModule = activeModuleKey
      ? findHelpModuleByKey(activeModuleKey)
      : findHelpModuleByPath(pathname);
    return currentModule ? buildModuleTourSteps(t, currentModule) : [];
  }, [activeModuleKey, pathname, t]);

  const helpManualTourSteps = useMemo(() => {
    const currentModule = activeModuleKey
      ? findHelpModuleByKey(activeModuleKey)
      : null;
    return currentModule ? buildHelpManualTourSteps(t, currentModule) : [];
  }, [activeModuleKey, t]);

  const tourSteps = activeTourKey === 'module'
    ? moduleTourSteps
    : activeTourKey === 'help-module'
      ? helpManualTourSteps
      : appTourSteps;

  const locale = useMemo(
    () => ({
      back: t('APP_TOUR.BACK'),
      close: t('APP_TOUR.CLOSE'),
      last: t('APP_TOUR.FINISH'),
      next: t('APP_TOUR.NEXT'),
      nextWithProgress: t('APP_TOUR.NEXT_WITH_PROGRESS'),
      skip: t('APP_TOUR.SKIP')
    }),
    [t]
  );

  const handleJoyrideEvent = useCallback((data, controls) => {
    const { action, index, status, type } = data;

    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      setRun(false);
      window.localStorage.setItem(TOUR_STORAGE_KEY, 'true');
      return;
    }

    if (type === EVENTS.TARGET_NOT_FOUND) {
      const nextIndex = action === ACTIONS.PREV ? index - 1 : index + 1;
      controls.go(nextIndex);
    }
  }, []);

  return (
    <Joyride
      key={tourKey}
      continuous
      run={run && tourSteps.length > 0}
      scrollToFirstStep
      steps={tourSteps}
      onEvent={handleJoyrideEvent}
      locale={locale}
      options={{
        beaconSize: 32,
        buttons: ['skip', 'back', 'close', 'primary'],
        closeButtonAction: 'skip',
        overlayClickAction: false,
        primaryColor: '#2c7be5',
        scrollOffset: 96,
        showProgress: true,
        spotlightRadius: 6,
        textColor: '#344050',
        width: 360,
        zIndex: 1080
      }}
    />
  );
};

export { PENDING_MODULE_TOUR_KEY, TOUR_START_EVENT };
export default AppJoyride;
